import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Bell, Volume2 } from 'lucide-react'
import type { RootState } from '../../store'
import { Header } from './Header'
import { Sidebar } from './Sidebar'
import { api } from '../../lib/api'
import {
  unlockNotificationSound,
  registerAdminPushToken,
} from '../../hooks/useAdminPushNotifications'

function notificationPermissionPending() {
  return 'Notification' in window &&
    Notification.permission === 'default' &&
    !localStorage.getItem('adminFcmToken')
}

function soundPermissionPending() {
  return localStorage.getItem('adminSoundEnabled') === null
}

function initialStep(): 'notification' | 'sound' | 'done' {
  if (notificationPermissionPending()) return 'notification'
  if (soundPermissionPending()) return 'sound'
  return 'done'
}

export function AdminShell() {
  const token = useSelector((root: RootState) => root.auth.token)
  const location = useLocation()
  const navigate = useNavigate()
  const [permissionStep, setPermissionStep] = useState<'notification' | 'sound' | 'done'>(initialStep)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const notificationId = params.get('notificationId')
    if (!notificationId) return

    api.patch(`/admin/notifications/${notificationId}/read`).catch(() => undefined)
    params.delete('notificationId')
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true },
    )
  }, [location.pathname, location.search, navigate])

  if (!token) return <Navigate replace to="/login" />

  function advanceFromNotification() {
    if (soundPermissionPending()) {
      setPermissionStep('sound')
    } else {
      setPermissionStep('done')
    }
  }

  return (
    <div className="flex min-h-svh bg-[#fbfaf6]">
      <Sidebar />
      <div className="min-w-0 flex-1">
        <Header />
        <main className="mx-auto max-w-[1600px] px-4 py-6 lg:px-6">
          <Outlet />
        </main>
        <footer className="border-t border-stone-200 px-6 py-4 text-xs text-stone-500">
          Seasonly Admin v1.0 · local environment
        </footer>
      </div>

      {permissionStep !== 'done' && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-black/40 p-4 backdrop-blur-sm">
          {permissionStep === 'notification' && (
            <section className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
              <div className="grid size-12 place-items-center rounded-lg bg-mango-yellow/20 text-leaf-dark">
                <Bell className="h-6 w-6" />
              </div>
              <h2 className="mt-4 text-xl font-bold text-leaf-dark">Allow order notifications</h2>
              <p className="mt-2 text-sm leading-6 text-stone-500">
                Get real-time alerts when new orders arrive, payments are received, or stock runs low.
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <button
                  className="rounded-lg border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50"
                  onClick={advanceFromNotification}
                >
                  Not now
                </button>
                <button
                  className="rounded-lg bg-leaf-dark px-4 py-2.5 text-sm font-semibold text-white hover:bg-leaf-dark/90"
                  onClick={async () => {
                    await registerAdminPushToken().catch(() => undefined)
                    advanceFromNotification()
                  }}
                >
                  Allow
                </button>
              </div>
            </section>
          )}

          {permissionStep === 'sound' && (
            <section className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
              <div className="grid size-12 place-items-center rounded-lg bg-mango-yellow/20 text-leaf-dark">
                <Volume2 className="h-6 w-6" />
              </div>
              <h2 className="mt-4 text-xl font-bold text-leaf-dark">Enable notification sound</h2>
              <p className="mt-2 text-sm leading-6 text-stone-500">
                Play an alert sound when new orders or broadcasts arrive, even when you're on another tab.
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <button
                  className="rounded-lg border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50"
                  onClick={() => {
                    localStorage.setItem('adminSoundEnabled', '0')
                    setPermissionStep('done')
                  }}
                >
                  Not now
                </button>
                <button
                  className="rounded-lg bg-leaf-dark px-4 py-2.5 text-sm font-semibold text-white hover:bg-leaf-dark/90"
                  onClick={async () => {
                    const unlocked = await unlockNotificationSound()
                    if (unlocked) setPermissionStep('done')
                  }}
                >
                  Enable sound
                </button>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  )
}
